<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { deleteRegistration, deleteSession, getNextSessions, getRegistrationsBySession, openSession } from '../../api';
  import type { IRegistration, IResult, ISession } from '../../api/entities';
  import { notifySuccess } from '../../stores/notifications';
  import { setTitle } from '../../stores/titles';
  import { formatDate } from '../../utils';
  import ButtonWithConfirm from '../ButtonWithConfirm.svelte';
  import Loader from '../Loader.svelte';
import Session from '../Session.svelte';

  let selectedSession = undefined;

  $: fetchSessions = getNextSessions(true);
  $: fetchRegistrations = undefined as (Promise<IResult & { registrations: any[] }>) | undefined;

  const getRegistrations = async (session: ISession) => {
    selectedSession = session;
    fetchRegistrations = getRegistrationsBySession(session._id);
  };

  const removeSession = async (sessionId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) {
      await deleteSession(sessionId);
      notifySuccess('la session a bien été supprimée !');
      fetchSessions = getNextSessions(true);
    }
  };

  const removeRegistration = async (registrationId: string) => {
    if (confirm('Êtes-vous sûr de vouloir annuler cette inscription ?')) {
      await deleteRegistration(registrationId);
      notifySuccess("l'inscription a bien été supprimée !");
      fetchRegistrations = getRegistrationsBySession(selectedSession._id);
    }
  };

  const openOrCloseSession = async (sessionId: string, open: boolean) => {
    await openSession(sessionId, open);
    notifySuccess(`la session est bien ${open ? 'ouverte' : 'fermée'} aux inscriptions`);
  };

  const getRegisterdListContent = (registrations: IRegistration[]) => {
    let csvContent = 'data:text/csv;charset=utf-8,'
      + 'NOM;TELEPHONE;NB ADULTES;AGE DES ENFANTS\n'
      + registrations.map(r => [r.name, r.phone, r.nbAdults, r.children.map(c => c.age).join(',')].join(';')).join('\n');

    return encodeURI(csvContent);
  };
  setTitle('Administration');
</script>

<style lang="scss">
  .table {
    td {
      width: 25%;
    }

    &.cols-5 {
      td {
        width: 22%;

        &.small {
          width: 12%;
        }
      }
    }

    :global(.button) {
      margin-bottom: 0;
      padding: 0;
      height: fit-content;
    }

    :global(.is-white, .is-inverted) {
      background-color: transparent !important;
    }
  }

  .no-height {
    height: unset;
  }
  </style>

  <section>
    {#await fetchSessions}
      <Loader />
    {:then sessionsResult}
      <article class="block container is-max-desktop">
        <div class="columns">
          <div class="column is-offset-8 has-text-right">
            <button class="button is-info" on:click={() => navigate('/admin/create-session')}>Nouvelle session</button>
          </div>
        </div>

        <h3 class="subtitle">Liste prochaines des sessions :</h3>

        <table class="table is-fullwidth is-small cols-5">
          <tbody>
            {#each sessionsResult.sessions as session}
            <tr class:is-selected={selectedSession?._id === session._id}>
              <td>
                <button class="button is-ghost" on:click={() => getRegistrations(session)} title={session.description}>{session.name}</button>
              </td>
              <td>{formatDate(session.date, { withDate: true, withTime: true })}</td>
              <td>
                {sessionsResult.registeredPlacesBySession[session._id]} inscrits ({session.nbPlaces > 0 ? `${session.nbPlaces} places` : 'illimité'})
              </td>
              <td class="small">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    class="checkbox"
                    bind:checked={session.open}
                    on:change={() => openOrCloseSession(session._id, session.open)}
                  />
                  Ouvert
                </label>
              </td>
              <td class="has-text-left">
                <ButtonWithConfirm click={() => removeSession(session._id)} />
              </td>
            </tr>
        {/each}
          </tbody>
        </table>
      </article>

      {#await fetchRegistrations}
        <Loader />
      {:then registrationsResult}
        {#if registrationsResult}
          <article class="block container is-max-desktop">
            <div class="columns">
              <div class="column is-offset-8 has-text-right">
                <a
                  class="button is-info no-height"
                  href={getRegisterdListContent(registrationsResult.registrations)}
                  download={`inscrits-${selectedSession.name}-${formatDate(selectedSession.date, { withDate: true, withTime: true })}.csv`}
                >
                  Télécharger la liste
                </a>
              </div>
            </div>

            <table class="table is-fullwidth">
              <thead>
                <th>Nom</th>
                <th>Téléphone</th>
                <th>Nombre d'adultes</th>
                <th>Age des enfants</th>
                <th></th>
              </thead>
              <tbody>
              {#each registrationsResult.registrations as registration}
              <tr>
                <td>{registration.name}</td>
                <td>{registration.phone}</td>
                <td>{registration.nbAdults}</td>
                <td>{registration.children.map(c => c.age).join(',')}</td>
                <td class="has-text-left">
                  <ButtonWithConfirm click={() => removeRegistration(registration._id)} />
                </td>
              </tr>
              {/each}
            </tbody>
            </table>
          </article>
        {/if}
      {/await}
    {/await}
  </section>
